import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Stack } from '@mui/material';

import { InfoField, InfoFieldChangeHandler } from '../../../../../components';

import {
  GetRateMarkupGroupResult,
  useGetFeeGoupsQuery,
  useGetRateMarkupGroupsQuery,
} from '../../../../../api';
import type { Profile, ProfileWithInfo } from '../../../../../types';

type FeesProps = {
  profile: Profile | undefined;
  isEditMode: boolean;
  fields: Partial<ProfileWithInfo> | null;
  fieldsHandler: InfoFieldChangeHandler;
  rateMarkupGroups: GetRateMarkupGroupResult | undefined;
  isLoading?: boolean;
};

export const FeesInfo: FC<FeesProps> = (props) => {
  const { isEditMode, profile, fields, fieldsHandler, rateMarkupGroups, isLoading } = props;
  const { t } = useTranslation();

  // api
  const { data: feeGroups } = useGetFeeGoupsQuery({});

  // elements
  const feeGroupsSelectOptions = feeGroups?.feeGroups.map(({ id, name }) => {
    return (
      <MenuItem key={id} value={id}>
        {name}
      </MenuItem>
    );
  });
  const rateMarkupGroupsSelectOptions = rateMarkupGroups?.rateMarkupGroups.map(({ id, name }) => {
    return (
      <MenuItem key={id} value={id}>
        {name}
      </MenuItem>
    );
  });

  const merchantRateMarkupGroupsMapped = rateMarkupGroups?.rateMarkupGroups.length
    ? [
        { id: -1, value: '' },
        ...rateMarkupGroups?.rateMarkupGroups?.map(({ id, name }) => ({ id, value: name })),
      ]
    : [{ id: -1, value: '' }];

  const merchantRateMarkupGroupsSelectOptions = merchantRateMarkupGroupsMapped.map(
    ({ id, value }) => {
      return (
        <MenuItem key={id} value={id} style={{ minHeight: '36px' }}>
          {value}
        </MenuItem>
      );
    }
  );

  return (
    <Stack direction="column" spacing={1}>
      <InfoField
        name={t('profile-details.fees.fee-group')}
        value={profile?.fee_group_id}
        formattedValue={
          profile?.fee_group_id
            ? feeGroups?.feeGroups.find((c) => c.id === profile?.fee_group_id)!.name
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'fee_group_id',
          value: fields?.fee_group_id ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: feeGroupsSelectOptions,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.fees.exchange-fee-group')}
        value={profile?.rate_markup_group_id}
        formattedValue={
          profile?.fee_group_id
            ? rateMarkupGroups?.rateMarkupGroups.find(
                (c) => c.id === profile?.rate_markup_group_id
              )!.name
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'rate_markup_group_id',
          value: fields?.rate_markup_group_id ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: rateMarkupGroupsSelectOptions,
        }}
        isLoading={isLoading}
        withoutDivider
      />
    </Stack>
  );
};
